import React, { Fragment, useEffect } from 'react';
import { isBefore, parse } from 'date-fns';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { defineMessages } from 'react-intl';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { Button, ButtonGroup, Checkbox, ErrorMessage, Form, Switch, TimePicker } from 'components/HookForm';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import { DAYS_OF_WEEK } from '../Items.constants';

import './ItemAvailabilityModal.scss';

const { block, element } = bem('item-availability-modal');

const messages = defineMessages({
  addTitle: {
    id: 'items.item-availability-modal.add-title',
    defaultMessage: 'Add item availability',
  },
  editTitle: {
    id: 'items.item-availability-modal.edit-title',
    defaultMessage: 'Edit item availability',
  },
  description: {
    id: 'items.item-availability-modal.description',
    defaultMessage: 'This is when this item will be available to order.',
  },
  updateAllAvailability: {
    id: 'items.item-availability-modal.update-all-availability',
    defaultMessage: 'Apply this availability to all items.',
  },
  addTimes: {
    id: 'items.item-availability-modal.add-times',
    defaultMessage: 'Add times',
  },
  startLabel: {
    id: 'items.item-availability-modal.start-label',
    defaultMessage: 'Start',
  },
  endLabel: {
    id: 'items.item-availability-modal.end-label',
    defaultMessage: 'End',
  },
  save: {
    id: 'items.item-availability-modal.save',
    defaultMessage: 'Save',
  },
  saveAndAddItem: {
    id: 'items.item-availability-modal.saveAndAddItem',
    defaultMessage: 'Save and add item',
  },
  cancel: {
    id: 'items.item-availability-modal.cancel',
    defaultMessage: 'Cancel',
  },
  Sunday: {
    id: 'items.item-availability-modal.sunday',
    defaultMessage: 'Sunday',
  },
  Monday: {
    id: 'items.item-availability-modal.monday',
    defaultMessage: 'Monday',
  },
  Tuesday: {
    id: 'items.item-availability-modal.tuesday',
    defaultMessage: 'Tuesday',
  },
  Wednesday: {
    id: 'items.item-availability-modal.wednesday',
    defaultMessage: 'Wednesday',
  },
  Thursday: {
    id: 'items.item-availability-modal.thursday',
    defaultMessage: 'Thursday',
  },
  Friday: {
    id: 'items.item-availability-modal.friday',
    defaultMessage: 'Friday',
  },
  Saturday: {
    id: 'items.item-availability-modal.saturday',
    defaultMessage: 'Saturday',
  },
  noAvailableDaysError: {
    id: 'items.item-availability-modal.noAvailableDays',
    defaultMessage: 'You must have at least 1 available day.',
  },
  startTimeAfterEndTime: {
    id: 'items.item-availability-modal.startTimeAfterEndTime',
    defaultMessage: 'Start time cannot be later than end time',
  },
  startTimeSameAsEndTime: {
    id: 'items.item-availability-modal.startTimeSameAsEndTime',
    defaultMessage: 'Start time cannot be the same as end time',
  },
  timePeriodsOverlap: {
    id: 'items.item-availability-modal.timePeriodsOverlap',
    defaultMessage: 'Time periods cannot overlap. Please adjust your selected times.',
  },
  required: {
    id: 'items.item-availability-modal.required',
    defaultMessage: 'This field is required.',
  },
});

export const ITEM_AVAILABILITY_MODAL_NAME = 'ItemAvailabilityModal';
const NO_AVAILABLE_DAYS_ERROR = 'noAvailability';

const getHasOverlappingTimes = (currentIndex, currentStartTime, currentEndTime, existingDayValues) => {
  const existingTimes = existingDayValues
    .filter((_, idx) => idx !== currentIndex)
    .map(({ startTime, endTime }) => ({
      startTime,
      endTime,
    }));

  const hasOverlap = existingTimes.some(({ startTime, endTime }) => {
    const existingStartTime = parse(startTime, 'HH:mm', new Date());
    const existingEndTime = parse(endTime, 'HH:mm', new Date());

    const isStartTimeWithinAnotherWindow = currentStartTime >= existingStartTime && currentStartTime < existingEndTime;
    const isEndTimeWithinAnotherWindow = currentEndTime <= existingEndTime && currentEndTime > existingStartTime;
    const isAnotherWindowContainedWithin = currentStartTime <= existingStartTime && currentEndTime >= existingEndTime;

    return isStartTimeWithinAnotherWindow || isEndTimeWithinAnotherWindow || isAnotherWindowContainedWithin;
  });

  return hasOverlap;
};

function ItemAvailabilityDay({ weekday }) {
  const isMobile = useSelector(selectIsMobile);
  const { control, getFieldState, formState, trigger, unregister, watch } = useFormContext();
  const { errors } = formState;
  const { fields, append, remove } = useFieldArray({ name: `${weekday}.times`, control });
  const available = watch(`${weekday}.available`);

  const showRemoveTimesButton = fields.length > 1;
  const showAddTimesButton = fields.length === 1 && !isMobile;
  const showAddTimesTextButton = fields.length > 1 || isMobile;

  useEffect(() => {
    if (!available) {
      unregister(`${weekday}.times`, { keepValue: true });
    }
  }, [available, unregister]);

  const validateTimeWindow = (currentIndex, startName, endName, weekdayName) => {
    if (!available) {
      return true;
    }

    const startTime = watch(startName);
    const endTime = watch(endName);

    if (!startTime || !endTime) {
      return false;
    }

    const parsedStartTime = parse(startTime, 'HH:mm', new Date());
    const parsedEndTime = parse(endTime, 'HH:mm', new Date());

    if (parsedStartTime.getTime() === parsedEndTime.getTime()) {
      return false;
    }
    if (!isBefore(parsedStartTime, parsedEndTime)) {
      return false;
    }

    const existingDayValues = watch(weekdayName);
    const hasOverlap = getHasOverlappingTimes(currentIndex, parsedStartTime, parsedEndTime, existingDayValues);

    if (hasOverlap) {
      return false;
    }
    return true;
  };

  const getTimeWindowValidationErrorMessage = (currentIndex, startName, endName, weekdayName) => {
    const startTime = watch(startName);
    const endTime = watch(endName);

    if (!startTime || !endTime) {
      return messages.required;
    }

    const parsedStartTime = parse(startTime, 'HH:mm', new Date());
    const parsedEndTime = parse(endTime, 'HH:mm', new Date());

    if (parsedStartTime.getTime() === parsedEndTime.getTime()) {
      return messages.startTimeSameAsEndTime;
    }
    if (!isBefore(parsedStartTime, parsedEndTime)) {
      return messages.startTimeAfterEndTime;
    }

    const existingDayValues = watch(weekdayName);
    const hasOverlap = getHasOverlappingTimes(currentIndex, parsedStartTime, parsedEndTime, existingDayValues);
    if (hasOverlap) {
      return messages.timePeriodsOverlap;
    }
  };

  return (
    <article className={element('day')}>
      <div className={element('weekday-wrapper')}>
        <Switch className={element('available-switch')} name={`${weekday}.available`} />
        <Message className={element('weekday')} message={messages[weekday]} />
      </div>
      <When predicate={available || !isMobile}>
        <div className={element('day-availabilities-container', { disabled: !available })}>
          {fields.map(({ id, startTime, endTime }, idx) => {
            const weekdayName = `${weekday}.times`;
            const startTimeName = `${weekday}.times[${idx}].startTime`;
            const endTimeName = `${weekday}.times[${idx}].endTime`;

            const showErrorForBothTimes = !!getFieldState(startTimeName).error && !!getFieldState(endTimeName).error;

            const isRemoveTimesHighlighted = !!watch(startTimeName) && !!watch(endTimeName);

            const removeTimes = () => {
              remove(idx);
              trigger();
            };

            const timeWindowValidator = {
              value: () => validateTimeWindow(idx, startTimeName, endTimeName, weekdayName),
              message: () => getTimeWindowValidationErrorMessage(idx, startTimeName, endTimeName, weekdayName),
            };

            return (
              <div key={id} className={element('day-availability-row')}>
                <div className={element('time-inputs-group', { showErrorForBothTimes })}>
                  <div className={element('time-inputs')}>
                    <TimePicker
                      className={element('time-picker')}
                      name={startTimeName}
                      defaultValue={startTime}
                      disabled={!available}
                      validate={timeWindowValidator}
                      onChange={() => trigger(endTimeName)}
                      toggleLabel={isMobile ? messages.startLabel : undefined}
                    />
                    <TimePicker
                      className={element('time-picker')}
                      name={endTimeName}
                      defaultValue={endTime}
                      disabled={!available}
                      validate={timeWindowValidator}
                      onChange={() => trigger(startTimeName)}
                      toggleLabel={isMobile ? messages.endLabel : undefined}
                    />
                  </div>
                  <When predicate={showErrorForBothTimes}>
                    <ErrorMessage
                      className={element('time-inputs-error-message')}
                      name={startTimeName}
                      errors={errors}
                    />
                  </When>
                </div>
                <When predicate={showAddTimesButton}>
                  <button
                    type="button"
                    className={element('icon-btn', { disabled: !available })}
                    disabled={!available}
                    onClick={() => append({ startTime: '', endTime: '' })}>
                    <Isvg src={ICONS.add} />
                  </button>
                </When>
                <When predicate={showRemoveTimesButton}>
                  <button type="button" className={element('icon-btn')} disabled={!available} onClick={removeTimes}>
                    <Isvg src={isRemoveTimesHighlighted && available ? ICONS.trashCanBlack : ICONS.trashCan} />
                  </button>
                </When>
              </div>
            );
          })}
          <When predicate={showAddTimesTextButton}>
            <button
              type="button"
              className={element('icon-btn', { addTimesTextBtn: true, disabled: !available })}
              disabled={!available}
              onClick={() => append({ startTime: '', endTime: '' })}>
              <Isvg src={ICONS.add} />
              <Message className={element('add-times-text', { disabled: !available })} message={messages.addTimes} />
            </button>
          </When>
        </div>
      </When>
    </article>
  );
}

ItemAvailabilityDay.propTypes = {
  weekday: PropTypes.oneOf(DAYS_OF_WEEK),
};

function ItemAvailabilityModal({ isEditForm = false, initialAvailability, onSubmit }) {
  const dispatch = useDispatch();

  function cancel() {
    dispatch(closeModal());
  }

  async function save({ updateAll, ...restFormValues }) {
    const formattedAvailabilityRecurrence = Object.entries(restFormValues)
      .filter(([, { available }]) => available)
      .map(([weekday, { times, available }]) => ({
        weekday,
        available,
        times,
      }));

    await onSubmit({ availabilityRecurrence: formattedAvailabilityRecurrence, updateAll });
  }

  const hasAvailableDays = availability => {
    return Object.values(availability).some(day => day.available);
  };

  const mapInitialAvailability = availability => {
    return Object.values(availability).reduce((acc, { available, times, weekday }) => {
      acc[weekday] = {
        available,
        times,
      };
      return acc;
    }, {});
  };

  const defaultValues = mapInitialAvailability(initialAvailability);
  return (
    <Form className={block()} defaultValues={defaultValues} useFormOverrides={{ mode: 'onChange' }}>
      {function FormRender({ clearErrors, formState, handleSubmit, setError, watch }) {
        const { errors, isSubmitting } = formState;

        const validateAvailableDays = availabilityRecurrence => {
          const hasAvailability = hasAvailableDays(availabilityRecurrence);
          if (!hasAvailability) {
            setError(NO_AVAILABLE_DAYS_ERROR);
            return;
          }
          clearErrors(NO_AVAILABLE_DAYS_ERROR);
        };

        useEffect(() => {
          const subscription = watch(values => {
            validateAvailableDays(values);
          });
          return () => subscription.unsubscribe();
        }, [watch]);

        return (
          <>
            <header className={element('header')}>
              <h3>
                <When predicate={!isEditForm}>
                  <Message message={messages.addTitle} />
                </When>
                <When predicate={isEditForm}>
                  <Message message={messages.editTitle} />
                </When>
              </h3>
              <Isvg src={ICONS.cross} className={element('close-icon')} onClick={cancel} />
            </header>
            <p className={element('description')}>
              <Message message={messages.description} />
              <When predicate={isEditForm}>
                <span className={element('update-all-wrapper')}>
                  <Checkbox className="mr--" name="updateAll" />
                  <Message message={messages.updateAllAvailability} />
                </span>
              </When>
            </p>
            <When predicate={!!errors.noAvailability}>
              <div className={element('error-banner')}>
                <Isvg className={element('error-icon')} src={ICONS.error} />
                <Message message={messages.noAvailableDaysError} />
              </div>
            </When>
            <section className={element('content')}>
              {DAYS_OF_WEEK.map(weekday => (
                <Fragment key={weekday}>
                  <ItemAvailabilityDay weekday={weekday} />
                  <div className={element('content-divider')} />
                </Fragment>
              ))}
            </section>
            <footer className={element('footer')}>
              <ButtonGroup className={element('button-group')} divider={false} flush>
                <Message
                  as={Button}
                  className={element('cancel-btn')}
                  disabled={isSubmitting}
                  message={messages.cancel}
                  onClick={cancel}
                  styled="tertiary"
                  type={Button.TYPES.BUTTON}
                />
                <Message
                  as={Button}
                  className={element('submit-btn', { addButton: !isEditForm })}
                  disabled={isSubmitting || !!errors.noAvailability}
                  message={isEditForm ? messages.save : messages.saveAndAddItem}
                  onClick={handleSubmit(save)}
                  styled="primary"
                  type={Button.TYPES.SUBMIT}
                />
              </ButtonGroup>
            </footer>
          </>
        );
      }}
    </Form>
  );
}

const ItemAvailabilityShape = {
  weekday: PropTypes.oneOf(DAYS_OF_WEEK),
  times: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    })
  ),
  available: PropTypes.bool,
};

ItemAvailabilityModal.propTypes = {
  isEditForm: PropTypes.bool,
  initialAvailability: PropTypes.arrayOf(PropTypes.shape(ItemAvailabilityShape)),
  onSubmit: PropTypes.func,
};

export default ItemAvailabilityModal;
